/**
 * @file: useOwnerFilter.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Lazily resolves the owner (assignee) filter. The roster is fetched on first
 * open — NOT on mount — so revealing the filter panel never suspends the whole
 * list (which caused a full-page reload flash). A short roster (< 10) picks via
 * an action sheet; a longer one routes to the searchable picker screen. Never
 * assume the roster is short.
 */

import { LeadsNavParams } from '@euler/app/flows/leads';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgMember } from '@euler/model/entity';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag } from '@euler/utils';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useRef } from 'react';

// Above this many members an action sheet is unwieldy — use the picker screen.
const kSheetThreshold = 10;

// DEV/TEST ONLY: flip to `true` to always route owner selection to the member
// picker SCREEN, bypassing the short-roster action sheet, so the picker-page
// flow can be exercised without a large roster. `safeMarkDevFlag` forces it off
// in production builds regardless of the passed value.
const kForceMemberPickerPage = safeMarkDevFlag(false);

export type OwnerSelection = { id: number; name: string } | undefined;

export const useOwnerFilter = (
  assignee: OwnerSelection,
  onSelect: (next: OwnerSelection) => void,
) => {
  const { storeService } = useServiceFactory();
  const loading = useAppLoading();
  const { showActionSheetWithOptions } = useActionSheet();
  const navigation = useNavigation<StackNavigationProp<LeadsNavParams>>();
  // Cache the roster for this picker's lifetime so reopening doesn't refetch.
  const roster = useRef<OrgMember[] | null>(null);

  const pickFromSheet = useCallback(
    (members: OrgMember[]) => {
      // The first option clears the filter; the rest map to a member.
      const labels = ['全部', ...members.map(m => m.name)];
      showActionSheetWithOptions(
        {
          options: [...labels, '取消'],
          cancelButtonIndex: labels.length,
          title: '负责人',
        },
        selected => {
          if (selected == null || selected >= labels.length) return;
          if (selected === 0) onSelect(undefined);
          else {
            const m = members[selected - 1];
            onSelect({ id: m.id, name: m.name });
          }
        },
      );
    },
    [onSelect, showActionSheetWithOptions],
  );

  const toPickerScreen = useCallback(() => {
    navigation.push('_serviceAgentSelector', {
      selectedServiceAgentId: assignee?.id,
      onSelect: (m: OrgMember) => onSelect({ id: m.id, name: m.name }),
    });
  }, [assignee?.id, navigation, onSelect]);

  return useCallback(async () => {
    // Dev override: skip the roster fetch + sheet and go straight to the screen.
    if (kForceMemberPickerPage) {
      toPickerScreen();
      return;
    }
    if (!roster.current) {
      await loading.showWithAction(async () => {
        roster.current = await storeService.listServiceAgents();
      });
    }
    const members = roster.current ?? [];
    if (members.length < kSheetThreshold) {
      pickFromSheet(members);
    } else {
      toPickerScreen();
    }
  }, [loading, pickFromSheet, storeService, toPickerScreen]);
};
